import type { PetDef } from '../../data/schema';
import { DEFAULT_BODY_HEIGHT_FACTOR } from '../config';
import { applyDamage, rollDamage } from '../combat';
import { ZERO_STATS, type Monster, type Pet, type Vec2 } from '../types';
import { clampToWalkable, entitiesOfKind, getPlayer, spawn, type World } from '../world';

/** Marge (px) ajoutée à la somme des rayons pour qu'un coup porte. */
const CONTACT_MARGIN = 4;
/** Tolérance (px) autour de la distance de suivi avant de se remettre en route. */
const FOLLOW_SLACK = 8;

/** Crée le familier à la position donnée : allié du joueur, 1 PV, jamais ciblé. */
export function spawnPet(world: World, def: PetDef, at: Vec2): Pet {
  return spawn<Pet>(world, {
    kind: 'pet', team: 'player', defId: def.id, sprite: def.sprite,
    pos: { ...at }, vel: { x: 0, y: 0 }, radius: def.radius, height: def.height ?? def.radius * DEFAULT_BODY_HEIGHT_FACTOR, facing: { x: 1, y: 0 },
    hp: 1, hpMax: 1, alive: true, anim: 'idle', animTime: 0, attackCooldown: 0,
  });
}

function moveToward(world: World, pet: Pet, def: PetDef, to: Vec2, dt: number): void {
  const dx = to.x - pet.pos.x, dy = to.y - pet.pos.y;
  const dist = Math.hypot(dx, dy);
  if (dist < 0.5) { pet.vel = { x: 0, y: 0 }; return; }
  const step = Math.min(def.speed * dt, dist);
  pet.facing = { x: dx / dist, y: dy / dist };
  pet.vel = { x: pet.facing.x * def.speed, y: pet.facing.y * def.speed };
  pet.pos = clampToWalkable({ x: pet.pos.x + pet.facing.x * step, y: pet.pos.y + pet.facing.y * step }, pet.radius, world.room, pet.pos);
}

/** Familier : attaque le monstre vivant le plus proche du joueur (dans la portée), sinon suit le joueur. */
export function petSystem(world: World, dt: number): void {
  const p = getPlayer(world);
  for (const pet of entitiesOfKind<Pet>(world, 'pet')) {
    if (!pet.alive) continue;
    const def = world.content.pets[pet.defId];
    if (!def) continue;
    pet.attackCooldown = Math.max(0, pet.attackCooldown - dt);
    if (!p || !p.alive) { pet.vel = { x: 0, y: 0 }; if (pet.anim === 'run') pet.anim = 'idle'; continue; }

    let target: Monster | null = null;
    let best = def.range;
    for (const m of entitiesOfKind<Monster>(world, 'monster')) {
      if (!m.alive) continue;
      const d = Math.hypot(m.pos.x - p.pos.x, m.pos.y - p.pos.y);
      if (d <= best) { best = d; target = m; }
    }

    if (target) {
      const dist = Math.hypot(target.pos.x - pet.pos.x, target.pos.y - pet.pos.y);
      const contact = pet.radius + target.radius + CONTACT_MARGIN;
      if (dist > contact) moveToward(world, pet, def, target.pos, dt);
      else {
        pet.vel = { x: 0, y: 0 };
        if (dist > 0) pet.facing = { x: (target.pos.x - pet.pos.x) / dist, y: (target.pos.y - pet.pos.y) / dist };
        if (pet.attackCooldown <= 0) {
          pet.attackCooldown = def.attackCooldown;
          pet.anim = 'attack'; pet.animTime = 0;
          applyDamage(world, target, rollDamage(world.rng, def.damage, ZERO_STATS, target.resistances, 1), def.damage.element, target.pos, 'pet');
          world.events.push({ type: 'petAttack', pos: { ...pet.pos } });
        }
      }
    } else {
      const dx = pet.pos.x - p.pos.x, dy = pet.pos.y - p.pos.y;
      const dist = Math.hypot(dx, dy);
      if (dist > def.followDistance + FOLLOW_SLACK) {
        const k = def.followDistance / dist;
        moveToward(world, pet, def, { x: p.pos.x + dx * k, y: p.pos.y + dy * k }, dt);
      } else pet.vel = { x: 0, y: 0 };
    }

    if (pet.anim === 'idle' && (pet.vel.x !== 0 || pet.vel.y !== 0)) pet.anim = 'run';
    else if (pet.anim === 'run' && pet.vel.x === 0 && pet.vel.y === 0) pet.anim = 'idle';
  }
}
